import React, { useState, useEffect } from "react";
import {
  DialogTitle,
  Box,
  IconButton,
  TextField,
  Typography,
  Fade,
  Link,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import LanguageIcon from "@mui/icons-material/Language";

const TitleSection = ({ 
  title, 
  url, 
  getDomain, 
  onUpdate, 
  authorized, 
  editMode, 
  onEditModeChange,
  rightPanelData,
  onRightPanelUpdate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tempTitle, setTempTitle] = useState(title);

  // Keep local title in sync if parent passes in new title
  useEffect(() => {
    setTempTitle(title);
  }, [title]);

  // Save handler
  const handleSave = () => {
    if (onUpdate) {
      // Include rating_id if it exists in rightPanelData
      const updateData = { title: tempTitle };
      if (rightPanelData?.rating_id) {
        updateData.rating_id = rightPanelData.rating_id;
      }
      onUpdate(updateData);
    }
    if (onRightPanelUpdate) {
      onRightPanelUpdate(rightPanelData); // send the updated right panel data
    }
    onEditModeChange(false);
  };

  // Cancel handler
  const handleCancel = () => {
    setTempTitle(title);
    onEditModeChange(false);
  };

  return (
    <Fade in timeout={500}>
      <DialogTitle
        sx={{
          textAlign: "center",
          py: { xs: 2, sm: 2.5, md: 3 },
          px: { xs: 2, sm: 2.5, md: 3 },
          borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
          background: "linear-gradient(145deg, #ffffff, #f8f9fa)",
        }}
      >
        {/* Title area: either editing mode or read-only display */}
        {editMode ? (
          <Box 
            sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 1.5, sm: 1 },
              width: "100%",
              maxWidth: { xs: "100%", sm: "600px" },
              mx: "auto",
            }}
          >
            <TextField
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />
            <Box sx={{ 
              display: "flex", 
              gap: 1,
              justifyContent: { xs: "flex-end", sm: "flex-start" },
            }}>
              <IconButton 
                onClick={handleSave} 
                size={isMobile ? "medium" : "small"}
                sx={{
                  color: "success.main",
                  minWidth: { xs: "44px", sm: "40px" },
                  minHeight: { xs: "44px", sm: "40px" },
                  "&:hover": {
                    backgroundColor: "rgba(46, 125, 50, 0.1)",
                  },
                }}
              >
                <SaveIcon fontSize={isMobile ? "medium" : "small"} />
              </IconButton>
              <IconButton 
                onClick={handleCancel} 
                size={isMobile ? "medium" : "small"}
                sx={{
                  color: "text.secondary",
                  minWidth: { xs: "44px", sm: "40px" },
                  minHeight: { xs: "44px", sm: "40px" },
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <ClearIcon fontSize={isMobile ? "medium" : "small"} />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", sm: "800px" },
              mx: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.5, sm: 1 },
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.125rem", sm: "1.375rem", md: "1.5rem" },
                color: "text.primary",
                lineHeight: { xs: 1.4, sm: 1.3 },
                textAlign: { xs: "center", sm: "left" },
                px: { xs: 0.5, sm: 0 },
                wordBreak: "break-word",
              }}
            >
              {title}
            </Typography>
            {authorized && (
              <IconButton
                size={isMobile ? "medium" : "small"}
                onClick={() => onEditModeChange(true)}
                sx={{
                  color: "primary.main",
                  minWidth: { xs: "44px", sm: "40px" },
                  minHeight: { xs: "44px", sm: "40px" },
                  "&:hover": {
                    backgroundColor: "rgba(18, 141, 111, 0.1)",
                  },
                }}
              >
                <EditIcon fontSize={isMobile ? "medium" : "small"} />
              </IconButton>
            )}
          </Box>
        )}

        {/* Domain link below title */}
        {url && (
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              mt: { xs: 1.5, sm: 2 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.5, sm: 0.5 },
              flexWrap: "wrap",
            }}
          >
            <LanguageIcon 
              sx={{ 
                fontSize: { xs: "0.875rem", sm: "1rem" }, 
                color: "text.secondary",
                opacity: 0.7,
              }} 
            />
            <Link
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
              sx={{
                cursor: "pointer",
                color: "text.secondary",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                textDecoration: "none",
                wordBreak: "break-all",
                px: { xs: 1, sm: 0 },
                minHeight: { xs: "44px", sm: "auto" },
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  color: "primary.main",
                  textDecoration: "underline",
                },
                "&:active": {
                  color: "primary.dark",
                },
              }}
            >
              {getDomain(url)}
            </Link>
          </Box>
        )}
      </DialogTitle>
    </Fade>
  );
};

export default TitleSection;
